import { CloudinaryImage } from "@cloudinary/url-gen/assets/CloudinaryImage";
import { Resize } from "@cloudinary/url-gen/actions";
import { Box, Stack, Typography, useTheme } from '@mui/material';

import GenericImpactMetric from 'src/components/impact/GenericImpactMetric';
import Image from 'src/components/Image';
import { getCloudinaryImageFromFullUrl } from 'src/utils/cloudinary';
import { AppGridContainer, AppGridContent, AppSection } from "src/components";
import { Project } from "src/resources/api/project/types.api";

//
//

interface Props {
  projects: Project[];
  subtitle?: string | React.ReactNode;
}

export const ReportProjects: React.FC<Props> = ({ projects, subtitle }) => {
  const theme = useTheme();

  return (
    <AppSection title="Supported projects" sx={{ p: 0 }}>
      <Typography variant="body2" sx={{ mb: 2 }}>
        {subtitle || (
          <>
            Below is a list of all projects that the programme has supported since the launch of the
            programme, including those that are no longer part of your current project portfolio.
          </>
        )}
      </Typography>

    <Stack direction="column" spacing={2} sx={{ p: 1 }}>
      {projects?.map((p: any) => {
        let myImage: null | CloudinaryImage = getCloudinaryImageFromFullUrl(p?.thumbnailImageUrl);
        let croppedSquareAutoFocusProjectUrl: null | string = myImage == null ? null :
          myImage.resize(Resize.fill(240, 140).gravity("auto")).toURL();

          //on the rare occasion that it's not a cloudinary url, use the original url
        if(!croppedSquareAutoFocusProjectUrl && p?.thumbnailImageUrl) croppedSquareAutoFocusProjectUrl = p?.thumbnailImageUrl;

        return p && <AppGridContainer key={p.id} spacing={0}>
          <AppGridContent size={{ xs: 12, sm: 4, lg: 3 }}>
            <Image
              src={croppedSquareAutoFocusProjectUrl || ''}
              sx={{ minHeight: 140, maxHeight: { xs: 200, sm: '100%' }, width: 'auto', height: { xs: 200, sm: '100%' } }}
              visibleByDefault
              alt={`${p?.name} project image`}
            />
          </AppGridContent>

          <AppGridContent size={{ xs: 12, sm: 8, lg: 9 }} p={1}>
            <Stack
              gap={1}
              direction={{ xs: 'column', lg: 'row' }}
              justifyContent={'space-between'}
              sx={{ height: '100%' }}
            >
              <Box sx={{ alignSelf: 'flex-start' }}>
                {p.name && (
                  <Typography gutterBottom sx={{ fontWeight: 'bold' }}>{p.name}</Typography>
                )}
                {p.shortDescription && (
                  <Typography variant='body2'>{p.shortDescription}</Typography>
                )}
              </Box>

              <GenericImpactMetric
                loading={false}
                items={[
                  {
                    data: { value: p?.impact?.numberOfContributions },
                    title: 'Total contributions',
                    className: 'primary',
                    key: 'CONTRIBUTIONS',
                  },
                  {
                    data: {
                      value: p?.impact?.co2Quantity.value,
                      unit: p?.impact?.co2Quantity.unit,
                    },
                    title: 'Amount of carbon mitigiated',
                    className: 'secondary',
                    key: 'CARBON',
                  },
                ]}
                sx={{
                  minWidth: 240,
                  gap: '8px !important',
                  '& .impact': {
                    alignItems: 'center',
                  },
                  '& .MuiTypography-h4': {
                    [theme.breakpoints.down('lg')]: {
                      fontSize: '18px !important',
                      marginLeft: -2,
                    },
                  },
                  '& .secondary svg, .primary svg': {
                    [theme.breakpoints.down('lg')]: {
                      transform: 'scale(0.7) !important',
                      WebkitTransformOrigin: 'left center',
                    },
                  },
                  [theme.breakpoints.down('md')]: {
                    justifyContent: 'flex-start',
                  },
                }}
              />
            </Stack>
          </AppGridContent>
        </AppGridContainer>
      })} 
      </Stack>
    </AppSection>
  );
};
